import { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Dialog from '@hiredigital/ui/Dialog/Dialog';

import dynamic from 'next/dynamic';
import Cookies from 'universal-cookie';

import Password from '@hiredigital/ui/Input/Password/Password';
import TextInput from '@hiredigital/ui/Input/SimpleTextInput';
import Button from '@hiredigital/ui/Button';
import Loader from '@hiredigital/ui/Loader';

import Link from '@components/Link';
import { submitHubspotForm } from '@components/Registration/common/helpers';

import { sendEvent } from '@lib/gtm.js';
import { validateEmail, identifyHubspotUser, getNameParts, getFullName } from '@helpers/forms';

import {
  getTalentRegistration,
  putTalentRegistration,
  submitTalentRegistration,
  postServiceRate,
  putServiceRate,
  postTalentRate,
  putTalentRate,
} from '@apis/registrations';
import { postRegistration } from '@apis/auth';

import { COOKIE_DOMAIN } from '@helpers/utils';
import { acceptedFiles } from '@hiredigital/lib/helpers/utils';

import Styles from './Registration.module.scss';

const isNewRates = /^true$/i.test(process.env.NEXT_PUBLIC_NEW_RATES_FLAG);
const SupportingForm = dynamic(() => import('./TalentRegistration/SupportingForm'), { ssr: false });
const RateForm = dynamic(() => import('./TalentRegistration/RateForm'), { ssr: false });
import { validateRateForm } from './TalentRegistration/RateForm';
const cookies = new Cookies();

const EngagementRegistration = ({ engagementUuid, engagement, search, initialOnboardStage }) => {
  const [uuid, setUuid] = useState(
    search?.talentUuid || cookies.get('talentUuid') || cookies.get('engagementTalentUuid')
  );
  const [fullName, setFullName] = useState(search?.name || null);
  const [password, setPassword] = useState(null);
  const [email, setEmail] = useState(search?.email || null);
  const [rate, setRate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [onboardStage, setOnboardStage] = useState(initialOnboardStage);
  const [lastOnboardStage, setLastOnboardStage] = useState(
    parseInt(cookies.get('engagementLastOnboardStage')) || 1
  );
  const [attachments, setAttachments] = useState([]);
  const [links, setLinks] = useState([]);
  const [serviceRateUuid, setServiceRateUuid] = useState('');
  const [rateType, setRateType] = useState(engagement.estimatedTalentRateType);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState(isNewRates ? '' : 'USD');
  const [resume, setResume] = useState('');
  const [resumeError, setResumeError] = useState(false);
  const [tryCount, setTryCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (uuid) {
      loadExistingData().then(() => {
        if (email) setShowModal(true);
      });
    }
  }, []);

  const loadExistingData = async (completionCallback) => {
    const result = await getTalentRegistration(uuid);
    if (!result?.data) return;
    populateFields(result.data, completionCallback);
  };

  const populateFields = (data, completionCallback) => {
    const { email, firstName, lastName, links, attachments, resume, serviceRates, talentRates } =
      data;
    setEmail(email);
    setFullName(getFullName(firstName, lastName));
    setLinks(links);
    setAttachments(attachments);
    setResume(resume);

    if (!isNewRates && !!serviceRates?.length) {
      setServiceRateUuid(serviceRates[0]?.uuid);
      setRate(parseFloat(serviceRates[0]?.rate));
      setCurrency(serviceRates[0]?.currency || 'USD');
      setRateType(serviceRates[0]?.rateType);
      setName(serviceRates[0]?.name);
    }

    if (isNewRates && !!talentRates?.length) {
      setServiceRateUuid(talentRates[0]?.uuid);
      setRate(parseFloat(talentRates[0]?.maxRate));
      setCurrency(talentRates[0]?.currencyCode || 'USD');
      setRateType(talentRates[0]?.rateType);
      setName(talentRates[0]?.name);
    }

    completionCallback?.();
    setResumeError(false);
  };

  const handleResumeError = () => {
    setResumeError(true);
  };

  useEffect(() => {
    cookies.set('engagementOnboardStage', onboardStage, { path: '/' });
    const lastStage = lastOnboardStage;
    const currentStage = onboardStage;
    if (lastStage <= currentStage) {
      setLastOnboardStage(currentStage);
      cookies.set('engagementLastOnboardStage', currentStage, { path: '/' });
    }
  }, [onboardStage]);

  const handleOpenModal = async () => {
    const { firstName, lastName } = getNameParts(fullName);
    const pageName = document.title;
    const pageUrl = window.location.href;

    if (!onboardStage) {
      // Hack for when onbordstage becomes undefined
      setOnboardStage(1);
    }

    if (validateInterestFormFields()) {
      setIsSubmitting(true);
      try {
        const data = {
          email,
          firstName,
          lastName,
          engagement: engagementUuid,
          site: 2,
        };
        const putResult = await putTalentRegistration(uuid, data);
        const newUuid = putResult?.data?.uuid;
        setUuid(newUuid);
        cookies.set('talentUuid', newUuid, { path: '/' });
        setShowModal(true);
        sendEvent({ event: 'GTM-TalentRegistration' });

        submitHubspotForm(process.env.NEXT_PUBLIC_TALENT_FORMID, pageUrl, pageName, {
          ...search,
          fullName,
          email,
          engagement: engagementUuid,
          talentType:
            engagement?.otherTalentType || engagement?.talentType?.label || 'No Talent Type',
          format: engagement?.otherFormat || engagement?.format?.label || 'No Format',
          site: 'Hire Digital',
        });
      } catch (error) {
        console.error(error);
        handleErrorResponse(error?.response?.data);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const validateInterestFormFields = () => {
    let valid = true;
    setIsRegistered(false);
    let stateErrors = {};

    if (!fullName) {
      stateErrors = { ...stateErrors, fullName: 'Enter your full name' };
      valid = false;
    }

    if (!email) {
      stateErrors = { ...stateErrors, email: 'Enter your email address' };
      valid = false;
    } else if (!validateEmail(email)) {
      stateErrors = { ...stateErrors, email: 'Enter a valid email address' };
      valid = false;
    }

    setErrors(stateErrors);
    return valid;
  };

  const validatePassword = () => {
    if (!password) {
      setErrors({ password: 'Please enter password' });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validatePassword()) return;

    const data = {
      engagement: engagementUuid,
      talent: uuid,
      site: 2,
      password1: password,
      password2: password,
    };

    // Identify User Block
    try {
      identifyHubspotUser({
        email: email,
        name: fullName,
      });
      sendEvent({ event: 'GTM-TalentRegistrationConfirm' });
    } catch (err) {
      console.error(err);
    }
    setErrors({});
    setIsSubmitting(true);
    postRegistration(data)
      .then(({ data }) => {
        setShowModal(false);
        setIsSubmitting(false);
        cookies.set('token', data.key, COOKIE_DOMAIN);
        setTimeout(() => {
          window.location.href = `${process.env.NEXT_PUBLIC_APP_URL}/engagements/${engagementUuid}`;
        }, 1000);
      })
      .catch((error) => {
        handleErrorResponse(error);
      });
  };

  const handleErrorResponse = (error) => {
    if (!error) {
      setShowModal(false);
      setIsSubmitting(false);
      return;
    }
    const alreadyReg = 'A user is already registered with this email address.';
    if (
      error?.nonFieldErrors &&
      !!error?.nonFieldErrors?.length &&
      (error?.nonFieldErrors[0].includes('already registered') ||
        error?.nonFieldErrors[0] === alreadyReg)
    ) {
      setIsRegistered(true);
      setShowModal(false);
      setIsSubmitting(false);
      setErrors({ ...errors, email: error?.nonFieldErrors[0] });
    } else {
      // handle field errors
      // handle different structure of error response
      // for /accounts/registration => fields error are within error.response.data,
      // for /talent-onboard => fields error are within error
      const errorResponse = (error.response && error.response.data) || error || {};
      const errorFields = Object.keys(errorResponse) || []; // extract the list of fields having errors

      const formFieldsTypes = {
        interestFields: ['firstName', 'lastName', 'fullName', 'email'], // these are the fields in the register interest form
        firstStageFields: ['resume', 'rate'],
        lastStageFields: ['password', 'password1'],
      };
      // these are the fields included in the error response that needs to be mapped within the state
      // other fields in the error response do have exact field name within the local state so no need to map them
      const mappers = { password1: 'password', rateType: 'rate' }; // we dont have rateType field visible so just map the error to rate field

      let stateErrors = {};
      errorFields.forEach((key) => {
        stateErrors = {
          ...stateErrors,
          [mappers[key] || key]: !!errorResponse[key]?.length && errorResponse[key][0],
        };
      });
      setIsSubmitting(false);
      setShowModal(
        !errorFields.some(
          // determine if we need to close the modal or not(if field error is in the register interest form we need to close the modal)
          (field) => formFieldsTypes.interestFields.includes(field)
        )
      );
      setErrors(stateErrors);
    }
  };

  const handleEnterPress = (event, forSubmit) => {
    if (event.key === 'Enter') {
      if (forSubmit) {
        handleSubmit();
      } else {
        handleOpenModal();
      }
    }
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const validateModalFormFields = (stage) => {
    setErrors({});

    if (stage === 1) {
      const hasResume = !!resume;
      const hasOneOrMoreTries = tryCount >= 1;

      if (hasResume || hasOneOrMoreTries) {
        return true;
      }
      setTryCount((tryCount) => tryCount + 1);

      return false;
    } else if (stage === 2) {
      const valid = validateRateForm({ rateType, currency, rate }, setErrors);
      if (!valid) setSubmitted(true);
      return valid;
    }
  };

  const handleResumeContinue = async () => {
    if (onboardStage === 1 && validateModalFormFields(1)) {
      setOnboardStage(2);
    } else if (onboardStage === 2 && validateModalFormFields(2)) {
      setIsSubmitting(true);
      Promise.all([rateUpdateOrCreate(uuid), submitTalentRegistration(uuid)])
        .then(([{ data: service }]) => {
          setOnboardStage(3);
          setIsSubmitting(false);
          setServiceRateUuid(service.uuid);
        })
        .catch((error) => {
          handleErrorResponse(error);
        });
    }
  };

  const rateUpdateOrCreate = (talentUuid) => {
    const data = isNewRates
      ? {
          maxRate: rate,
          rateType,
          name,
          currencyCode: currency || 'USD',
        }
      : {
          rate,
          rateType,
          name,
          currency: currency || 'USD',
        };

    if (serviceRateUuid) {
      return (isNewRates ? putTalentRate : putServiceRate)(talentUuid, serviceRateUuid, data);
    } else {
      return (isNewRates ? postTalentRate : postServiceRate)(talentUuid, data);
    }
  };

  const handleRateChange = (rate) => {
    setSubmitted(false);
    setErrors({});
    setRate(rate);
  };

  const handleCurrencyChange = (currency) => setCurrency(currency);

  const handleRateTypeChange = (value) => setRateType(value.id);

  const handleRateDescriptionChange = (event) => setName(event.target.value);

  const handleBack = () => {
    setOnboardStage((onboardStage) => onboardStage - 1);
    setErrors({});
    setTryCount(0);
  };

  return (
    <Fragment>
      <Dialog
        isOpen={showModal}
        hasClose={false}
        onClose={handleClose}
        bodyClass={Styles.engagementDialog}>
        {onboardStage === 1 && (
          <SupportingForm
            embedInDialog
            onSubmit={handleResumeContinue}
            onResponse={populateFields}
            onError={handleResumeError}
            talentUuid={uuid}
            loading={isSubmitting}
            {...{
              resume,
              acceptedFiles,
              resumeError,
              attachments,
              links,
              tryCount,
            }}
          />
        )}
        {onboardStage === 2 && (
          <RateForm
            embedInDialog
            onSubmit={handleResumeContinue}
            onBack={handleBack}
            onCurrencyChange={handleCurrencyChange}
            onRateChange={handleRateChange}
            onRateTypeChange={handleRateTypeChange}
            onRateDescriptionChange={handleRateDescriptionChange}
            loading={isSubmitting}
            {...{
              currency,
              rate,
              submitted,
              errors,
              rateType,
              name,
            }}
          />
        )}
        {onboardStage === 3 && (
          <Fragment>
            <h3 className={Styles.headline}>{`Add a password`}</h3>
            <p className={Styles.description}>
              {`If you would like to view more details about the engagement or submit a proposal, create a Hire Digital account.`}
            </p>
            <div className={classNames(Styles.inputContainer, Styles.spaceBottom)}>
              <Password
                name='password'
                value={password}
                minLength={6}
                placeholder={`Password`}
                autoFocus
                showStrength
                error={errors?.password}
                onChange={(event) => setPassword(event.target.value)}
                onKeyPress={(e) => handleEnterPress(e, true)}
              />
            </div>

            <p className={Styles.smallNote}>
              {`By creating your account, you agree to Hire Digital's `}
              <Link href='/terms'>{`Terms of Service`}</Link>
              {` & `}
              <Link href='/privacy'>{`Privacy Policy`}</Link>
            </p>

            <div className={Styles.talentActionRow}>
              <Button type={Button.Type.WHITE} onClick={handleBack}>
                {'Back'}
              </Button>
              <Button
                style={{ marginBlock: 0 }}
                type={Button.Type.BLUE}
                size={Button.Size.FULLWIDTH_LARGE}
                onClick={handleSubmit}>
                {isSubmitting && (
                  <Loader
                    className={classNames(Styles.loader, Styles.spaceRight)}
                    color={Loader.Color.WHITE}
                    size={Loader.Size.SMALL}
                  />
                )}
                {`Submit`}
              </Button>
            </div>
          </Fragment>
        )}
      </Dialog>

      <div className={Styles.interestForm}>
        <h3 className={Styles.clientDetailsSectionTitle}>{`Register your Interest`}</h3>
        <div className={classNames(Styles.inputContainer, Styles.spaceBottom)}>
          <TextInput
            name='fullName'
            label='Full Name'
            error={errors?.fullName}
            value={fullName || ''}
            onChange={(event) => setFullName(event.target.value)}
          />
        </div>
        <div
          className={classNames(Styles.inputContainer, Styles.spaceBottom)}
          style={{ position: 'relative' }}>
          <TextInput
            name='email'
            label='Email Address'
            error={validateEmail() || errors?.email}
            value={email || ''}
            onChange={(event) => setEmail(event.target.value)}
            onKeyPress={(e) => handleEnterPress(e)}
          />
          {isRegistered && (
            <div className={Styles.inputAltAction}>
              <a
                className={Styles.errorMsgLink}
                title=''
                href={`${process.env.NEXT_PUBLIC_SITE_URL}/login?email=${email}`}>
                {`Login`}
              </a>
            </div>
          )}
        </div>

        <Button
          style={{ marginTop: '10px' }}
          type={Button.Type.BLUE}
          size={Button.Size.FULLWIDTH_LARGE}
          onClick={handleOpenModal}>
          {isSubmitting && (
            <Loader
              className={classNames(Styles.loader, Styles.spaceRight)}
              color={Loader.Color.WHITE}
              size={Loader.Size.SMALL}
            />
          )}
          {`Register Interest`}
        </Button>
      </div>
    </Fragment>
  );
};

EngagementRegistration.propTypes = {
  search: PropTypes.object,
  engagementUuid: PropTypes.string,
  engagement: PropTypes.object,
  initialOnboardStage: PropTypes.number,
};

export default EngagementRegistration;
